import React, { useState, useEffect, useCallback } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassBadge } from '../ui/GlassBadge';
import { GlassSelect } from '../ui/GlassSelect';
import { apiClient } from '../../services/api';
import { Bot, Cpu, Zap, Brain, Info, RefreshCw, CheckCircle, Trash2, Image, Film } from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Types ──────────────────────────────────────────────────────────────────────

interface SupportedModel {
  provider: string;
  model_id: string;
  label: string;
  default: boolean;
  cost_tier?: string;
}

interface ModelsCatalogue {
  text: SupportedModel[];
  coordinator: SupportedModel[];
  image?: SupportedModel[];
  video?: SupportedModel[];
}

interface UserPreference {
  model_type: string;
  provider: string;
  model_id: string;
  label: string | null;
}

interface UserApiKeyMeta {
  provider: string;
  is_validated: boolean;
  validated_at: string | null;
  last_error: string | null;
}

interface SystemDefault {
  model_type: string;
  provider: string | null;
  model_id: string | null;
  label: string | null;
}

interface UserModelConfig {
  preferences: UserPreference[];
  api_keys: UserApiKeyMeta[];
  system_defaults: SystemDefault[];
}

interface FallbackEntry {
  id: number;
  model_type: string;
  attempted_provider: string;
  error_summary: string | null;
  fell_back_to: string | null;
  created_at: string | null;
}

type Phase1ModelType = 'text' | 'coordinator' | 'image' | 'video';

// ── Constants ─────────────────────────────────────────────────────────────────

const PREF_TYPES: Phase1ModelType[] = ['text', 'coordinator', 'image', 'video'];

const MODEL_TYPE_LABELS: Record<Phase1ModelType, string> = {
  text: 'Text Generation',
  coordinator: 'Coordinator Brain',
  image: 'Image Generation',
  video: 'Video Generation',
};

const PROVIDER_LABELS: Record<string, string> = {
  gemini: 'Gemini',
  claude: 'Claude',
  openai: 'OpenAI',
  grok: 'Grok',
  dall_e: 'DALL-E / OpenAI',
  stability: 'Stability AI',
  replicate: 'Replicate',
  runway: 'RunwayML',
  pika: 'Pika Labs',
  kling: 'Kling AI',
};

const PROVIDER_ICONS: Record<string, React.ReactNode> = {
  gemini: <Zap size={16} />,
  claude: <Brain size={16} />,
  openai: <Bot size={16} />,
  grok: <Cpu size={16} />,
  dall_e: <Image size={16} />,
  stability: <Image size={16} />,
  replicate: <Image size={16} />,
  runway: <Film size={16} />,
  pika: <Film size={16} />,
  kling: <Film size={16} />,
};

// Platform providers don't need a user API key
const PLATFORM_PROVIDERS = new Set(['gemini']);

// Providers shown in the text/coordinator API key section
const TEXT_PROVIDERS = ['gemini', 'claude', 'openai', 'grok'];

// Providers shown in the image API key section
const IMAGE_PROVIDERS = ['dall_e', 'stability', 'replicate'];

// Providers shown in the video API key section
const VIDEO_PROVIDERS = ['runway', 'pika', 'kling'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function getUniqueProviders(models: SupportedModel[]): string[] {
  const seen: string[] = [];
  for (const m of models) {
    if (!seen.includes(m.provider)) seen.push(m.provider);
  }
  return seen;
}

// ── Component ─────────────────────────────────────────────────────────────────

const AIModelPreferences: React.FC = () => {
  const [userConfig, setUserConfig] = useState<UserModelConfig | null>(null);
  const [catalogue, setCatalogue] = useState<ModelsCatalogue | null>(null);
  const [fallbackLog, setFallbackLog] = useState<FallbackEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Per-provider key state: { [provider]: { inputValue, saving, error } }
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [keySaving, setKeySaving] = useState<Record<string, boolean>>({});
  const [keyErrors, setKeyErrors] = useState<Record<string, string>>({});
  const [keySuccess, setKeySuccess] = useState<Record<string, boolean>>({});
  const [keyRemoving, setKeyRemoving] = useState<Record<string, boolean>>({});

  // Per-model-type pref state
  const [prefProvider, setPrefProvider] = useState<Record<string, string>>({});
  const [prefModelId, setPrefModelId] = useState<Record<string, string>>({});
  const [prefSaving, setPrefSaving] = useState<Record<string, boolean>>({});
  const [prefError, setPrefError] = useState<Record<string, string>>({});
  const [prefResetting, setPrefResetting] = useState<Record<string, boolean>>({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, catRes, logRes] = await Promise.all([
        apiClient.get('/model-config/user'),
        apiClient.get('/model-config/models'),
        apiClient.get('/model-config/user/fallback-log'),
      ]);
      const config: UserModelConfig = configRes.data;
      setUserConfig(config);
      setCatalogue(catRes.data);
      setFallbackLog(logRes.data.slice(0, 5));

      // Initialise pref dropdowns from saved preferences
      const providerMap: Record<string, string> = {};
      const modelMap: Record<string, string> = {};
      for (const p of config.preferences) {
        providerMap[p.model_type] = p.provider;
        modelMap[p.model_type] = p.model_id;
      }
      // Default dropdowns to system defaults for types with no saved pref
      for (const sd of config.system_defaults) {
        if (!providerMap[sd.model_type] && sd.provider) {
          providerMap[sd.model_type] = sd.provider;
        }
        if (!modelMap[sd.model_type] && sd.model_id) {
          modelMap[sd.model_type] = sd.model_id;
        }
      }
      setPrefProvider(providerMap);
      setPrefModelId(modelMap);
    } catch (err) {
      console.error('Failed to load AI model config', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Provider key actions ───────────────────────────────────────────────────

  const handleSaveKey = async (provider: string) => {
    const key = keyInputs[provider] || '';
    if (!key.trim()) {
      setKeyErrors(prev => ({ ...prev, [provider]: 'Please enter an API key' }));
      return;
    }
    setKeySaving(prev => ({ ...prev, [provider]: true }));
    setKeyErrors(prev => ({ ...prev, [provider]: '' }));
    setKeySuccess(prev => ({ ...prev, [provider]: false }));
    try {
      const res = await apiClient.post('/model-config/user/api-key', { provider, api_key: key });
      const updated: UserApiKeyMeta = res.data;
      // Update local state
      setUserConfig(prev => {
        if (!prev) return prev;
        const filtered = prev.api_keys.filter(k => k.provider !== provider);
        return { ...prev, api_keys: [...filtered, updated] };
      });
      setKeyInputs(prev => ({ ...prev, [provider]: '' }));
      setKeySuccess(prev => ({ ...prev, [provider]: true }));
      setTimeout(() => setKeySuccess(prev => ({ ...prev, [provider]: false })), 4000);
    } catch (err: any) {
      const detail = getApiError(err, 'Failed to save key');
      setKeyErrors(prev => ({ ...prev, [provider]: detail }));
    } finally {
      setKeySaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleRemoveKey = async (provider: string) => {
    if (!window.confirm(`Remove your ${PROVIDER_LABELS[provider] || provider} API key? Any model preferences using this provider will also be reset.`)) return;
    setKeyRemoving(prev => ({ ...prev, [provider]: true }));
    try {
      await apiClient.delete(`/model-config/user/api-key/${provider}`);
      setUserConfig(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          api_keys: prev.api_keys.filter(k => k.provider !== provider),
          preferences: prev.preferences.filter(p => p.provider !== provider),
        };
      });
      // Clear pref dropdowns that used this provider
      setPrefProvider(prev => {
        const next = { ...prev };
        for (const [mt, prov] of Object.entries(next)) {
          if (prov === provider) delete next[mt];
        }
        return next;
      });
      setPrefModelId(prev => {
        const next = { ...prev };
        for (const mt of Object.keys(next)) {
          if (prefProvider[mt] === provider) delete next[mt];
        }
        return next;
      });
    } catch (err: any) {
      console.error('Failed to remove key', err);
    } finally {
      setKeyRemoving(prev => ({ ...prev, [provider]: false }));
    }
  };

  // ── Model preference actions ───────────────────────────────────────────────

  const handleProviderChange = (modelType: string, newProvider: string) => {
    setPrefProvider(prev => ({ ...prev, [modelType]: newProvider }));
    // Reset model_id to first available for this provider+type
    const models = (catalogue as any)?.[modelType] as SupportedModel[] | undefined;
    if (models) {
      const first = models.find(m => m.provider === newProvider);
      if (first) setPrefModelId(prev => ({ ...prev, [modelType]: first.model_id }));
    }
  };

  const handleSavePref = async (modelType: Phase1ModelType | string) => {
    const provider = prefProvider[modelType];
    const modelId = prefModelId[modelType];
    if (!provider || !modelId) return;
    setPrefSaving(prev => ({ ...prev, [modelType]: true }));
    setPrefError(prev => ({ ...prev, [modelType]: '' }));
    try {
      const res = await apiClient.put(`/model-config/user/${modelType}`, { provider, model_id: modelId });
      const updated = res.data;
      setUserConfig(prev => {
        if (!prev) return prev;
        const filtered = prev.preferences.filter(p => p.model_type !== modelType);
        return { ...prev, preferences: [...filtered, updated] };
      });
    } catch (err: any) {
      const detail = getApiError(err, 'Failed to save preference');
      setPrefError(prev => ({ ...prev, [modelType]: detail }));
    } finally {
      setPrefSaving(prev => ({ ...prev, [modelType]: false }));
    }
  };

  const handleResetPref = async (modelType: Phase1ModelType | string) => {
    setPrefResetting(prev => ({ ...prev, [modelType]: true }));
    try {
      await apiClient.delete(`/model-config/user/${modelType}`);
      setUserConfig(prev => {
        if (!prev) return prev;
        return { ...prev, preferences: prev.preferences.filter(p => p.model_type !== modelType) };
      });
      // Restore dropdown to system default
      const sd = userConfig?.system_defaults.find(d => d.model_type === modelType);
      if (sd?.provider) {
        setPrefProvider(prev => ({ ...prev, [modelType]: sd.provider! }));
        setPrefModelId(prev => ({ ...prev, [modelType]: sd.model_id || '' }));
      }
    } catch (err: any) {
      console.error('Failed to reset preference', err);
    } finally {
      setPrefResetting(prev => ({ ...prev, [modelType]: false }));
    }
  };

  // ── Helpers for render ─────────────────────────────────────────────────────

  const getApiKeyMeta = (provider: string): UserApiKeyMeta | undefined =>
    userConfig?.api_keys.find(k => k.provider === provider);

  const hasSavedPref = (modelType: string): boolean =>
    !!userConfig?.preferences.find(p => p.model_type === modelType);

  const getSystemDefault = (modelType: string): SystemDefault | undefined =>
    userConfig?.system_defaults.find(d => d.model_type === modelType);

  const isProviderAvailable = (provider: string): boolean =>
    PLATFORM_PROVIDERS.has(provider) || !!getApiKeyMeta(provider)?.is_validated;

  // Providers available to use in model pref dropdowns
  const getAvailableProviders = (modelType: string): string[] => {
    const models = (catalogue as any)?.[modelType] as SupportedModel[] | undefined;
    if (!models) return [];
    return getUniqueProviders(models).filter(isProviderAvailable);
  };

  const getModelsForProviderType = (modelType: string, provider: string): SupportedModel[] => {
    const models = (catalogue as any)?.[modelType] as SupportedModel[] | undefined;
    if (!models) return [];
    return models.filter(m => m.provider === provider);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <GlassCard variant="default" padding="lg" className="profile-section-card">
        <div className="section-header"><h2>AI Models</h2></div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Loading...</p>
      </GlassCard>
    );
  }

  return (
    <GlassCard variant="default" padding="lg" className="profile-section-card">
      <div className="section-header">
        <h2><Bot size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />AI Models</h2>
      </div>

      {/* ── Section 1: Provider API Keys ─────────────────────────────────── */}
      <div className="aim-subsection">
        <div className="aim-subsection-header">
          <h3>Provider API Keys</h3>
          <p className="aim-subtitle">
            Add your own API keys. Your key is encrypted and never shared.
            Leave blank to use the platform default.
          </p>
        </div>

        <div className="aim-provider-list">
          {TEXT_PROVIDERS.map(provider => {
            const meta = getApiKeyMeta(provider);
            const saving = keySaving[provider] || false;
            const removing = keyRemoving[provider] || false;
            const errMsg = keyErrors[provider] || '';
            const success = keySuccess[provider] || false;
            const icon = PROVIDER_ICONS[provider] || <Cpu size={16} />;
            const label = PROVIDER_LABELS[provider] || provider;

            return (
              <div key={provider} className="aim-provider-row">
                <div className="aim-provider-identity">
                  <span className="aim-provider-icon">{icon}</span>
                  <span className="aim-provider-name">{label}</span>
                  {meta?.is_validated ? (
                    <GlassBadge variant="success">
                      <CheckCircle size={12} style={{ marginRight: 3 }} />
                      Validated {formatDate(meta.validated_at)}
                    </GlassBadge>
                  ) : (
                    <GlassBadge variant="default">Not configured</GlassBadge>
                  )}
                </div>
                <div className="aim-provider-actions">
                  <GlassInput
                    type="password"
                    value={keyInputs[provider] || ''}
                    onChange={(val) => setKeyInputs(prev => ({ ...prev, [provider]: val }))}
                    placeholder="Paste API key…"
                  />
                  <GlassButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleSaveKey(provider)}
                    loading={saving}
                    disabled={saving || removing}
                  >
                    Save &amp; Validate
                  </GlassButton>
                  {meta?.is_validated && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRemoveKey(provider)}
                      loading={removing}
                      disabled={saving || removing}
                    >
                      <Trash2 size={14} style={{ marginRight: 3 }} />
                      Remove
                    </GlassButton>
                  )}
                </div>
                {errMsg && <div className="aim-inline-error">{errMsg}</div>}
                {success && (
                  <div className="aim-inline-success">
                    <CheckCircle size={14} style={{ marginRight: 4 }} />
                    Key validated and saved
                  </div>
                )}
              </div>
            );
          })}

          {/* Image generation provider keys */}
          <div className="aim-provider-section-label">
            <Image size={14} style={{ marginRight: 6 }} />
            Image Generation Providers
          </div>
          {IMAGE_PROVIDERS.map(provider => {
            const meta = getApiKeyMeta(provider);
            const saving = keySaving[provider] || false;
            const removing = keyRemoving[provider] || false;
            const errMsg = keyErrors[provider] || '';
            const success = keySuccess[provider] || false;
            const icon = PROVIDER_ICONS[provider] || <Image size={16} />;
            const label = PROVIDER_LABELS[provider] || provider;

            return (
              <div key={provider} className="aim-provider-row">
                <div className="aim-provider-identity">
                  <span className="aim-provider-icon">{icon}</span>
                  <span className="aim-provider-name">{label}</span>
                  {meta?.is_validated ? (
                    <GlassBadge variant="success">
                      <CheckCircle size={12} style={{ marginRight: 3 }} />
                      Validated {formatDate(meta.validated_at)}
                    </GlassBadge>
                  ) : (
                    <GlassBadge variant="default">Not configured</GlassBadge>
                  )}
                </div>
                <div className="aim-provider-actions">
                  <GlassInput
                    type="password"
                    value={keyInputs[provider] || ''}
                    onChange={(val) => setKeyInputs(prev => ({ ...prev, [provider]: val }))}
                    placeholder="Paste API key…"
                  />
                  <GlassButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleSaveKey(provider)}
                    loading={saving}
                    disabled={saving || removing}
                  >
                    Save &amp; Validate
                  </GlassButton>
                  {meta?.is_validated && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRemoveKey(provider)}
                      loading={removing}
                      disabled={saving || removing}
                    >
                      <Trash2 size={14} style={{ marginRight: 3 }} />
                      Remove
                    </GlassButton>
                  )}
                </div>
                {provider === 'dall_e' && (
                  <div className="aim-provider-note">
                    <Info size={12} style={{ marginRight: 4, flexShrink: 0 }} />
                    Your OpenAI API key works here too — if you already saved one above for text models, save it again here to enable DALL-E image generation.
                  </div>
                )}
                {errMsg && <div className="aim-inline-error">{errMsg}</div>}
                {success && (
                  <div className="aim-inline-success">
                    <CheckCircle size={14} style={{ marginRight: 4 }} />
                    Key validated and saved
                  </div>
                )}
              </div>
            );
          })}

          {/* Video generation provider keys */}
          <div className="aim-provider-section-label">
            <Film size={14} style={{ marginRight: 6 }} />
            Video Generation Providers
          </div>
          {VIDEO_PROVIDERS.map(provider => {
            const meta = getApiKeyMeta(provider);
            const saving = keySaving[provider] || false;
            const removing = keyRemoving[provider] || false;
            const errMsg = keyErrors[provider] || '';
            const success = keySuccess[provider] || false;
            const icon = PROVIDER_ICONS[provider] || <Film size={16} />;
            const label = PROVIDER_LABELS[provider] || provider;

            return (
              <div key={provider} className="aim-provider-row">
                <div className="aim-provider-identity">
                  <span className="aim-provider-icon">{icon}</span>
                  <span className="aim-provider-name">{label}</span>
                  {meta?.is_validated ? (
                    <GlassBadge variant="success">
                      <CheckCircle size={12} style={{ marginRight: 3 }} />
                      Validated {formatDate(meta.validated_at)}
                    </GlassBadge>
                  ) : (
                    <GlassBadge variant="default">Not configured</GlassBadge>
                  )}
                </div>
                <div className="aim-provider-actions">
                  <GlassInput
                    type="password"
                    value={keyInputs[provider] || ''}
                    onChange={(val) => setKeyInputs(prev => ({ ...prev, [provider]: val }))}
                    placeholder="Paste API key…"
                  />
                  <GlassButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleSaveKey(provider)}
                    loading={saving}
                    disabled={saving || removing}
                  >
                    Save &amp; Validate
                  </GlassButton>
                  {meta?.is_validated && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRemoveKey(provider)}
                      loading={removing}
                      disabled={saving || removing}
                    >
                      <Trash2 size={14} style={{ marginRight: 3 }} />
                      Remove
                    </GlassButton>
                  )}
                </div>
                {errMsg && <div className="aim-inline-error">{errMsg}</div>}
                {success && (
                  <div className="aim-inline-success">
                    <CheckCircle size={14} style={{ marginRight: 4 }} />
                    Key validated and saved
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Section 2: Model Preferences ─────────────────────────────────── */}
      <div className="aim-subsection">
        <div className="aim-subsection-header">
          <h3>Model Preferences</h3>
          <p className="aim-subtitle">
            Override the platform default for each content type.
            Only providers with a saved key are selectable.
          </p>
        </div>

        <div className="aim-pref-list">
          {PREF_TYPES.map(modelType => {
            const label = MODEL_TYPE_LABELS[modelType];
            const sd = getSystemDefault(modelType);
            const savedPref = hasSavedPref(modelType);
            const availableProviders = getAvailableProviders(modelType);
            const currentProvider = prefProvider[modelType] || '';
            const currentModelId = prefModelId[modelType] || '';
            const modelsForProvider = getModelsForProviderType(modelType, currentProvider);
            const saving = prefSaving[modelType] || false;
            const resetting = prefResetting[modelType] || false;
            const errMsg = prefError[modelType] || '';

            const providerOptions = availableProviders.map(p => ({
              value: p,
              label: PROVIDER_LABELS[p] || p,
            }));

            const modelOptions = modelsForProvider.map(m => ({
              value: m.model_id,
              label: m.label,
            }));

            return (
              <div key={modelType} className="aim-pref-row">
                <div className="aim-pref-info">
                  <span className="aim-pref-label">{label}</span>
                  {sd?.provider && (
                    <span className="aim-pref-default">
                      System default: {PROVIDER_LABELS[sd.provider] || sd.provider} — {sd.label}
                    </span>
                  )}
                </div>
                <div className="aim-pref-controls">
                  <GlassSelect
                    options={providerOptions}
                    value={currentProvider}
                    onChange={(val) => handleProviderChange(modelType, val)}
                    placeholder="Provider…"
                  />
                  <GlassSelect
                    options={modelOptions}
                    value={currentModelId}
                    onChange={(val) => setPrefModelId(prev => ({ ...prev, [modelType]: val }))}
                    placeholder="Model…"
                  />
                  <GlassButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleSavePref(modelType)}
                    loading={saving}
                    disabled={saving || resetting || !currentProvider || !currentModelId}
                  >
                    Save
                  </GlassButton>
                  {savedPref && (
                    <button
                      className="aim-reset-link"
                      onClick={() => handleResetPref(modelType)}
                      disabled={resetting}
                    >
                      {resetting ? (
                        <RefreshCw size={12} className="aim-spinning" style={{ marginRight: 4 }} />
                      ) : null}
                      Reset to default
                    </button>
                  )}
                </div>
                {errMsg && <div className="aim-inline-error">{errMsg}</div>}
              </div>
            );
          })}

        </div>
      </div>

      {/* ── Section 3: Fallback Activity ──────────────────────────────────── */}
      <div className="aim-subsection">
        <div className="aim-subsection-header">
          <h3>Fallback Activity</h3>
          <p className="aim-subtitle">
            When your preferred model is unavailable, the platform falls back automatically.
          </p>
        </div>

        {fallbackLog.length === 0 ? (
          <div className="aim-empty-state">
            <Info size={16} style={{ marginRight: 6, opacity: 0.6 }} />
            No fallback events
          </div>
        ) : (
          <div className="aim-fallback-table-wrap">
            <table className="aim-fallback-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Your Model</th>
                  <th>Fell Back To</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {fallbackLog.map(entry => (
                  <tr key={entry.id}>
                    <td>{formatDate(entry.created_at)}</td>
                    <td>{MODEL_TYPE_LABELS[entry.model_type as Phase1ModelType] || entry.model_type}</td>
                    <td>{PROVIDER_LABELS[entry.attempted_provider] || entry.attempted_provider}</td>
                    <td>{entry.fell_back_to || '—'}</td>
                    <td className="aim-fallback-reason">{entry.error_summary || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </GlassCard>
  );
};

export default AIModelPreferences;
